/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, FormEvent } from 'react';
import { motion } from 'motion/react';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/components/AuthProvider';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname ?? '/';

  // Reactive navigation: fires AFTER AuthProvider has committed auth state to React.
  // This replaces the old profile-fetch-then-navigate approach in handleSubmit, which
  // had a race condition where navigate('/') fired before setUser/setLoading were
  // committed, causing RequireAuth to see user=null and redirect back to /login.
  const { user, onboardingComplete, loading: authLoading } = useAuth();
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (onboardingComplete === null) return; // still loading from DB
    navigate(onboardingComplete ? from : '/onboarding', { replace: true });
  }, [user, onboardingComplete, authLoading, navigate, from]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
      setLoading(false);
      if (authError) {
        setError(authError.message);
      }
      // Navigation is handled by the useEffect above — AuthProvider will update
      // user/onboardingComplete state after SIGNED_IN fires, and the effect navigates.
    } catch (err: unknown) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError('Enter your email address first.'); return; }
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);
    if (resetError) { setError(resetError.message); }
    else { setError(''); alert('Password reset email sent!'); }
  };

  const inputClasses = "w-full bg-white/50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-base font-bold text-[#1A1F36] focus:outline-none focus:border-[#4A6CF7]/40 focus:bg-white/60 transition-all placeholder:text-black/20";
  const labelClasses = "block uppercase text-[10px] font-bold tracking-[0.2em] text-[#45556c] mb-1.5 ml-1";
  const primaryBtnClasses = "w-full bg-black text-white py-4 rounded-full font-black uppercase text-[10px] tracking-[0.2em] hover:scale-[1.05] active:scale-[0.95] transition-all shadow-xl shadow-black/10 flex items-center justify-center gap-2 disabled:opacity-40 disabled:scale-100";

  return (
    <div className="min-h-screen w-full bg-[hsl(225,30%,92%)] flex items-center justify-center p-6 font-sans selection:bg-black/5">
      <div className="w-full max-w-[1400px] flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative w-full max-w-lg bg-white/75 backdrop-blur-[28px] border border-white/65 shadow-xl shadow-blue-900/5 overflow-hidden rounded-[2.5rem]"
        >
          {/* Top progress bar */}
          <div className="absolute top-0 left-0 w-full h-1 bg-[#E3E8F1]">
            <motion.div
              className="h-full bg-[#4A6CF7]"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
            />
          </div>

          <div className="p-8 md:p-12 flex flex-col">
            <div className="flex flex-col space-y-8 pb-8">

              {/* Header */}
              <div className="space-y-1 flex flex-col items-start text-left w-full">
                <h1 className="text-5xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
                  Welcome <br />
                  <span className="text-[#4A6CF7]">back!</span>
                </h1>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#45556c] mt-4">
                  Log into your freelance command center.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="group relative">
                  <label className={labelClasses}>Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                    <input type="email" placeholder="name@example.com" value={email} onChange={e => setEmail(e.target.value)} className={inputClasses} required />
                  </div>
                </div>

                <div className="group relative">
                  <label className={labelClasses}>Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                    <input type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={inputClasses} required />
                    <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-black/20 hover:text-[#4A6CF7] transition-colors focus:outline-none">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest text-center">{error}</p>
                )}

                <div className="pt-6 text-center space-y-4">
                  <button type="submit" className={primaryBtnClasses} disabled={loading}>
                    {loading ? 'Signing in…' : 'Continue'} <ArrowRight size={14} className="opacity-50" />
                  </button>
                  <button type="button" onClick={handleForgotPassword} className="block w-full text-[9px] font-black uppercase tracking-[0.2em] text-[#4A6CF7] hover:underline transition-all">
                    Forgot Password?
                  </button>
                </div>
              </form>

              {/* Social */}
              <div className="space-y-6 pt-2">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-black/5"></div>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#1A1F36]/30">Or login with</span>
                  <div className="h-px flex-1 bg-black/5"></div>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    await supabase.auth.signInWithOAuth({
                      provider: 'google',
                      options: { redirectTo: `${window.location.origin}/` },
                    });
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3.5 border border-gray-200 rounded-2xl hover:bg-black/5 transition-all bg-white/40"
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="Google" />
                  <span className="text-[10px] font-black uppercase tracking-widest">Continue with Google</span>
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await supabase.auth.signOut({ scope: 'local' });
                    navigate('/onboarding');
                  }}
                  className="block w-full text-center text-[9px] font-black uppercase tracking-[0.2em] text-[#4A6CF7] hover:underline transition-all"
                >
                  New to FirstHold? Sign Up
                </button>
              </div>

            </div>
          </div>
        </motion.div>

        {/* Logo */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex justify-center mt-8">
          <img
            src="https://res.cloudinary.com/dlmbskxuc/image/upload/c_crop,w_175,h_170/v1745797729/logo1h_pmrhki.png"
            alt="Firsthold"
            className="w-9 h-9 pointer-events-none"
          />
        </motion.div>

        {/* Footer */}
        <div className="text-center pb-8 pt-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/20">
            © 2026 FirstHold &nbsp;·&nbsp;
            <a href="/privacy" className="hover:opacity-60 transition-opacity">Privacy Policy</a>
            &nbsp;·&nbsp;
            <a href="/terms" className="hover:opacity-60 transition-opacity">Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
}
